import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'
import moment from 'moment'
import DataWorker from 'util/data.worker'
import { serialize, deepCopy, getRasterDatesFromApi, createRandomLayerSignature, getRangeOfDates } from 'util/helpers'
import log from 'util/log'
import _ from 'lodash'
import { getRenderPixel } from "ol/render"
import { i18n } from './i18n.js' 

Vue.use(Vuex)

// set worker
const worker = new DataWorker()

export default new Vuex.Store({
  state: {
    config: {},
    status: '',
    token: window.localStorage.getItem(window.app.id + '.login.token') || '',
    user: window.localStorage.getItem(window.app.id + '.login.user') || '',
    baseLayer: null,
    overlayLayer:null,
    tileSetConf: null,
    tileSetProperty: null,
    searchResults: [],
    parcel: null,
    parcelStatus: null,
    parcelEvents: [],
    parcelLastRow: null,
    feature: null,
    timebar_value: null,
    filter: {
      0: true,
      1: true,
      2: true
    },
    comparisonData: {
      isComparisonModeActive: false,
      layers: [undefined, undefined],
      mapLayers: null,
      overlap: 50,
      updated: new Date().toISOString()
    },
    chartDataStorage: {
      datasets: [],
      timeLabels: []
    },
    chartData: null,
    chartImagery: {
      date: null,
      dates: null,
      tsGroup: null,
      parcelId: null
    },
    about: false,
    searchIndex: 0,
    mapDateIndex: 0,
    mapDates: [],
    mapTime: [],
    // WMS request to display in WMS button
    wmsRequest: null
  },
  mutations: {
    config (state, config) {
      state.config = config
    },
    status (state, value) {
      state.status = value
    },
    auth_success (state, user) {
      state.token = user.token
      state.user = user.username
    },
    logout (state) {
      state.status = ''
      state.token = ''
      state.user = ''
      state.parcel = null
      state.feature = null
    },
    baseLayerChanged (state, layer) {
      state.baseLayer = layer
    },
    overlayLayerChanged (state, layer) {
      state.overlayLayer = layer
    },
    tileSetChanged (state, conf) {
      state.tileSetConf = conf
    },
    tileSetPropertyChanged (state, set) {
      state.tileSetProperty = set
    },
    search_success (state, results) {
      state.searchResults = results
    },
    parcel_status (state, status) {
      state.parcelStatus = status
    },
    parcel_events (state, events) {
      state.parcelEvents = events
    },
    parcel_last_row (state, row) {
      state.parcelLastRow = row
    },
    feature (state, feature) {
      state.feature = feature
    },
    timebar (state, value) {
      state.timebar_value = value
    },
    filter (state, value) {
      state.filter = value
    },
    chartData (state, result) {
      state.chartData = result
    },
    addChartDataSets (state, datasets) {
      state.chartDataStorage.datasets = state.chartDataStorage.datasets.concat(datasets)
    },
    setChartTimeLabels (state, labels) {
      state.chartDataStorage.timeLabels = labels
    },
    clearChartDataStorage (state) {
      state.chartDataStorage.datasets = []
      state.timeLabels = []
    },
    chartImageryDate (state, date) {
      state.chartImagery.date = date
    },
    chartImageryDates (state, dates) {
      state.chartImagery.dates = dates
    },
    chartImageryTsGroup (state, group) {
      state.chartImagery.tsGroup = group
    },
    chartImageryParcelId (state, id) {
      state.chartImagery.parcelId = id
    },
    mapDateIndexChanged (state, index) {
      // Validate and update mapDateIndex and mapTime
      if (index < 0) {
        index = 0
      } else if (index >= state.mapDates.length) {
        index = state.mapDates.length - 1
      }
      state.mapDateIndex = index
      if (state.mapDates.length !== 0)
        state.mapTime = state.mapDates[index][1]
      else
        state.mapTime = []
    },
    clearMapDatesOnLayerChange (state) {
      state.mapDateIndex = 0
      state.mapDates = []
      state.mapTime = []
    },
    setMapDates (state, dates) {
      state.mapDates = dates
    },
    setMapTime (state, time) {
      state.mapTime = time
    },
    about (state, value) {
      state.about = value
    },
    parcel (state, row) {
      state.parcel = row
    },
    searchIndex (state, idx) {
      state.searchIndex = idx
    },
    setPublicWms (state, request) {
      state.wmsRequest = request
    },
    addLayerToCompare (state, value) {
      state.comparisonData.layers[value.index] = value.layer
      state.comparisonData.updated = new Date().toISOString()
    },
    removeLayerToCompare (state, id) {
      state.comparisonData.layers[id] = undefined
      state.comparisonData.isComparisonModeActive = false
      state.comparisonData.mapLayers.getLayers().clear()
      state.comparisonData.updated = new Date().toISOString()
    },
    swapLayersToCompare (state) {
      [state.comparisonData.layers[0], state.comparisonData.layers[1]] = [state.comparisonData.layers[1], state.comparisonData.layers[0]]
      state.comparisonData.updated = new Date().toISOString()
    },
    enterComparisonMode (state) {
      state.comparisonData.isComparisonModeActive = true
      state.comparisonData.updated = new Date().toISOString()
    },
    leaveComparisonMode (state) {
      state.comparisonData.isComparisonModeActive = false
      state.comparisonData.mapLayers.getLayers().clear()
      state.comparisonData.layers = [undefined, undefined]
      state.comparisonData.updated = new Date().toISOString()
    },
    setComparisonOverlap (state, overlap) {
      state.comparisonData.overlap = overlap
    },
    initializeLayersToCompareGroup (state, group) {
      state.comparisonData.mapLayers = group
    }
  },
  actions: {
    config ({ commit }, config) {
      commit('config', config)
    },
    setMapTime ({commit, state}, value) {
      commit('setMapTime', value)
    },
    updateMapDatesOnLayerChange ({commit, state, dispatch, getters}) {
      return new Promise((resolve, reject) => {
        let requestLayer = state.config.map.baseLayer
        if (this.getters['baseLayer'] != null) {
          requestLayer = this.getters['baseLayer']
        }

        getRasterDatesFromApi(state.config, requestLayer).then(mapDates => {
          if (mapDates.length == 0) {
            log('danger', i18n.t('noAcquisitions'))
          }

          let newMapDatesGroupedUtc = _.groupBy(mapDates, function (date) {
            return moment.utc(date).startOf('day').toISOString()
          })
          newMapDatesGroupedUtc = Object.entries(newMapDatesGroupedUtc)
          
          if (state.mapDates && state.mapDateIndex) {
            const currentMapDate = getters['mapDate']
            const newMapDatesList = newMapDatesGroupedUtc.map(d => d[0])
            let newMapDateIndex = null

            if (newMapDatesList.includes(currentMapDate)) {
              // First case, if the date can be kept, but the length is different
              newMapDateIndex = newMapDatesList.findIndex(date => date === currentMapDate)
            } else if (typeof(newMapDatesList.find(date => date > currentMapDate)) !== "undefined") {
              // Second case, if the date canot be kept, look for a closest in the future
              const newMapDate = newMapDatesList.find(date => date > currentMapDate)
              newMapDateIndex = newMapDatesList.findIndex(date => date === newMapDate)
            } else {
              // Third case, take the last avaliable
              newMapDateIndex = newMapDatesGroupedUtc.length - 1
            }
            commit('setMapDates', newMapDatesGroupedUtc)
            commit('mapDateIndexChanged', newMapDateIndex)
          } else {
            // Take the last avaliable on initialization
            let newMapDateIndex = newMapDatesGroupedUtc.length - 1
            commit('setMapDates', newMapDatesGroupedUtc)
            commit('mapDateIndexChanged', newMapDateIndex)
          }

          dispatch('updatePublicWmsRequest')
          return resolve()
        })
      })
    },
    clearMapDatesOnLayerChange ({commit, state}) {
      commit('clearMapDatesOnLayerChange')
    },
    status ({ commit, state}) {
      return new Promise((resolve, reject) => {
        axios.get(state.config.api.url + 'status')
          .then(resp => {
            resolve(resp)
          }).catch(err => {
            reject(err)
          })
      })
    },
    login ({ commit, state }, user) {
      return new Promise((resolve, reject) => {
        const token = 'Basic ' + btoa(user.username + ':' + user.password)
        axios.get(state.config.api.url + 'status', {
          withCredentials: true,
          auth: user
        }).then(resp => {
          if (user.rememberme) {
            window.localStorage.setItem(window.app.id + '.login.token', token)
            window.localStorage.setItem(window.app.id + '.login.user', user.username)
            window.localStorage.setItem(window.app.id + '.login.expire', (Math.floor(Date.now() / 1000) + (state.config.api.expire * 3600)))
          }
          axios.defaults.headers.common['Authorization'] = token
          commit('auth_success', { username: user.username, token: token })
          resolve(resp)
        }).catch(err => {
          window.localStorage.removeItem(window.app.id + '.login.token')
          window.localStorage.removeItem(window.app.id + '.login.user')
          window.localStorage.removeItem(window.app.id + '.login.expire')
          reject(err)
        })
      })
    },
    logout ({ commit }) {
      return new Promise((resolve, reject) => {
        commit('logout')
        window.localStorage.removeItem(window.app.id + '.login.token')
        window.localStorage.removeItem(window.app.id + '.login.user')
        window.localStorage.removeItem(window.app.id + '.login.expire')
        delete axios.defaults.headers.common['Authorization']
        resolve()
      })
    },
    setBaseLayer ({ commit, state }, layer) {
      commit('baseLayerChanged', layer)
    },
    setOverlayLayer ({ commit, state }, layer) {
      commit('overlayLayerChanged', layer)
    },
    loadTileSetConf ({ commit, state }, tileset) {
      return new Promise((resolve, reject) => {
        axios.get(state.config.tilesets.url + state.config.tilesets.key + '/' + tileset + '/metadata.json')
          .then(resp => {
            resp.data.id = tileset
            resp.data.bounds = resp.data.bounds.split(',').map(c => Number(c))
            resp.data.center = resp.data.center.split(',').map(c => Number(c))
            resp.data.minzoom = Number(resp.data.minzoom)
            resp.data.maxzoom = Number(resp.data.maxzoom)
            commit('tileSetChanged', resp.data)
            resolve(resp.data)
          }).catch(err => {
            reject(err)
          })
      })
    },
    changeTileSetProperty ({ commit, state }, set) {
      commit('tileSetPropertyChanged', set)
    },
    search ({ commit, state }, input) {
      commit('searchIndex', input.index)
      return new Promise((resolve, reject) => {
        const searchConf = state.config.search[input.index]
        const apiEndpoint = state.config.api.endpoints[searchConf.request]
        const params = deepCopy(apiEndpoint['params'])
        const request_path = apiEndpoint['request_path']
        const response_path = apiEndpoint['response_path']
        params[searchConf.query] = input.query
        params['properties'] = apiEndpoint['properties'].join(',')
        Object.keys(params).forEach(key => {
          if (params[key] === null) {
            delete params[key]
          }
        })
        axios.get(state.config.api.url + request_path + '?' + serialize(params))
        .then(resp => resp.data)
          .then(resp => {
            const data = resp[response_path]
            if (data && data.length) {
              // geometry text to json
              data.forEach(row => {
                if (typeof row.geometry === 'string') {
                  row.geometry = JSON.parse(row.geometry)
                }
              })
              commit('search_success', data)
              resolve(data)
            } else {
              reject()
            }
          }).catch(err => {
            reject(err)
          })
      })
    },
    selectedFeature ({ commit, state }, feature) {
      commit('feature', feature)
    },
    clearSearch ({ commit, state }) {
      commit('search_success', [])
      commit('feature', null)
      commit('parcel_status', null)
      commit('parcel_events', [])
      commit('parcel', null)
    },
    timebar ({ commit, state }, value) {
      commit('timebar', value)
    },
    filter ({ commit, state }, filter) {
      commit('filter', filter)
    },
    loadParcelEvents ({ commit, state }, id) {
      const request = 'parcel_estimate_changes'
      return new Promise((resolve, reject) => {
        const apiEndpoint = state.config.api.endpoints[request]
        const params = deepCopy(apiEndpoint['params'])
        const request_path = apiEndpoint['request_path']
        const response_path = apiEndpoint['response_path']
        params.parcel_foreign_id = id
        params['properties'] = apiEndpoint['properties'].join(',')
        axios({baseURL: state.config.api.url, url: request_path, method: 'get', params: params})
        .then(resp => resp.data)
          .then(resp => {
            const data = resp[response_path]
            if (data && data.length) {
              const data = data
                .filter(row => row.estimate === 'cut')
                .sort((a, b) => {
                  return moment(a.s1product_end_time).diff(b.s1product_end_time)
                })
                .map(row => {
                  if (row.s1product_end_time) {
                    row.date = moment(row.s1product_end_time).format('YYYY-MM-DD')
                  }
                  return row
                })
              commit('parcel_events', data)
              resolve(data)
            } else {
              reject()
            }
          }).catch(err => {
            reject(err)
          })
      })
    },
    loadParcelStatus ({ commit, state }, id) {
      const request = 'parcel_status_last'
      return new Promise((resolve, reject) => {
        const apiEndpoint = state.config.api.endpoints[request]
        const params = deepCopy(apiEndpoint['params'])
        const request_path = apiEndpoint['request_path']
        const response_path = apiEndpoint['response_path']
        params.parcel_foreign_id = id
        params['properties'] = apiEndpoint['properties'].join(',')
        axios({baseURL: state.config.api.url, url: request_path, method: 'get', params: params})
        .then(resp => resp.data)
          .then(resp => {
            const data = resp[response_path]
            if (data && data.length) {
              const data = data.map(row => {
                if (row.s1product_end_time) {
                  row.date = moment(row.s1product_end_time).format('YYYY-MM-DD')
                }
                return row
              })
              commit('parcel_status', data[0])
              resolve(data[0])
            } else {
              reject()
            }
          }).catch(err => {
            reject(err)
          })
      })
    },
    loadParcelLastRow ({ commit, state }, id) {
      const request = 'parcel_estimate_last'
      return new Promise((resolve, reject) => {
        const apiEndpoint = state.config.api.endpoints[request]
        const params = deepCopy(apiEndpoint['params'])
        const request_path = apiEndpoint['request_path']
        const response_path = apiEndpoint['response_path']
        params.parcel_foreign_id = id
        params['properties'] = apiEndpoint['properties'].join(',')
        axios({baseURL: state.config.api.url, url: request_path, method: 'get', params: params})
        .then(resp => resp.data)
          .then(resp => {
            const data = resp[response_path]
            if (data && data.length) {
              const data = data.map(row => {
                if (row.s1product_end_time) {
                  row.date = moment(row.s1product_end_time).format('YYYY-MM-DD')
                }
                return row
              })
              commit('parcel_last_row', data[0])
              resolve(data[0])
            } else {
              reject()
            }
          }).catch(err => {
            reject(err)
          })
      })
    },
    loadChartData ({ state, dispatch }, id) {
      return new Promise ((resolve, reject) => {
        const requests = state.config.chart.request
        const result = {
          id: id,
          data: []
        }
        var leftLimit = null
        if (state.config.chart.cutoffValue != null)
          leftLimit = moment().subtract(state.config.chart.cutoffValue, 'months')

        // Make requests specified in the config
        var r = 0
        requests.forEach((request, i = 1) => {
          const apiEndpoint = state.config.api.endpoints[request.endpoint]
          const request_path = apiEndpoint['request_path']
          const response_path = apiEndpoint['response_path']
          const params = Object.assign(deepCopy(apiEndpoint['params']), request.params)
          params.parcel_foreign_id = id
          params.properties = params.properties.join(',')
          
          // Request all statistics we want to have
          axios({baseURL: state.config.api.url, url: request_path, method: 'get', params: params})
          .then(response => response.data)
          .then(response => {
            const data = response[response_path]
            // Sort the response and save to the result
            result.data[i] = data.sort((a, b) => {
              return moment(a[request.dateColumn]).diff(b[request.dateColumn])
            })
            // These conditions cut received time-series beyond cutoffValue (in months) starting from now() date
            // It assumes that we have the last statistics date close to the real date, otherwise chart will be empty.
            // To ignore the clipping cutoffValue can be set as null.
            if (leftLimit != null) {
              result.data[i] = result.data[i].filter(dataEntry => moment(dataEntry[request.dateColumn]).diff(leftLimit) > 0)
            }
            
            // At the end of requesting invoke processing
            r = r + 1
            if (r === requests.length) {
              dispatch('processChartData', result).then((processedData) => {
                resolve(processedData)
              }).catch(error => {
                reject(error)
              })
            }
          }).catch(error => {
            console.log(error)
            reject(error)
          })
        })
      })
    },
    processChartData ({ commit, state, getters }, resultObject) {
      const resultData = resultObject.data
      return new Promise((resolve, reject) => {
        var start = null
        var end = null
        var days = []

        var datasets = state.config.chart.lines.map(line => {
          var dateColumn = state.config.chart.request[line.request].dateColumn
          start = moment(resultData[line.request][0][dateColumn])
          end = moment(resultData[line.request][0][dateColumn])

          const data = resultData[line.request].map(d => {
            const m = moment(d[state.config.chart.request[line.request].dateColumn])
            if (m < start) {
              start = m
            }
            if (end < m) {
              end = m
            }
            var pointDate = m.format('YYYY-MM-DD')
            var requestDate = m.format(state.config.chart.request[line.request].dateFormat)

            return {
              x: pointDate,
              y: d[line.id],
              requestDate: requestDate
            }
          })

          days = getRangeOfDates(start, end, 'day')

          return {
            id: resultObject.id,
            yAxisID: 'left',
            borderWidth: 1,
            pointBorderColor: '#FFFFFF', // Point border color
            backgroundColor: '#FFFFFF', // Point filling
            borderColor: '#FFFFFF', // Line color
            spanGaps: line.params.spanGaps ? line.params.spanGaps : false,
            click: line.params.click ? line.params.click : false,
            pointRadius: line.params.pointRadius ? line.params.pointRadius : 3,
            pointStyle: 'circle',
            data: data,
            lineTension: 0,
            label: i18n.t(`chart.legend.${line.id}`),
            lineId: line.id
          }
        })

        var timeLabels = [...days].map(d => d.format('YYYY-MM-DD'))

        commit('addChartDataSets', datasets)
        commit('setChartTimeLabels', timeLabels)
        resolve(datasets)
      })
    },
    chartImageryDate ({ commit }, date) {
      commit('chartImageryDate', date)
    },    
    chartImageryDates ({ commit }, dates) {
      commit('chartImageryDates', dates)
    },
    chartImageryTsGroup ({ commit }, group) {
      commit('chartImageryTsGroup', group)
    },
    chartImageryParcelId ({ commit }, id) {
      commit('chartImageryParcelId', id)
    },
    // Trigger a change in mapDateIndex and mapDate variables.
    setMapDateIndex ({ commit, state }, index) {
      commit('mapDateIndexChanged', index)
    },
    updatePublicWmsRequest ({ commit, state, getters }){
      if (state.baseLayer) {
        if (state.config.map.layers.baseLayers[state.baseLayer].layerType !== 'clear') {
          // If it is not an empty layer, update the whole thing
          let base_url = state.config.map.layers.baseLayers[state.baseLayer].layers[0].url
          if (!base_url.endsWith('/'))
            base_url += '/'
          let timeParameter = getters['mapDate'].split('T')[0]
          commit('setPublicWms', base_url + '?TIME=' + timeParameter)
        }
      }
    },
    addLayerToCompare ({ commit, state, getters }) {
      let layerToCompare = {
        id: createRandomLayerSignature(),
        layer: state.baseLayer,
        title: getters['config'].map.layers.baseLayers[state.baseLayer].title,
        time: state.mapTime,
        timeString: (function() {
          let times = state.mapTime.map((timeEntry, index) => {
            if (index == 0) {
              let date = timeEntry.split('T')[0]
              let time = timeEntry.split('T')[1].split('.')[0]
              return date + ' ' + time
            } else {
              return timeEntry.split('T')[1].split('.')[0]
            }
          })
          return times.join(', ')
        })()
      }

      for (let s = 0; s < getters['layersToCompare'].length; s++) {
        if (getters['layersToCompare'][s] === undefined) {
          commit('addLayerToCompare', { index:s, layer:layerToCompare })
          break
        }
      }
    },
    removeLayerToCompare ({ commit }, id) {
      commit('removeLayerToCompare', id)
    },
    swapLayersToCompare ({ commit }) {
      commit('swapLayersToCompare')
    },
    leaveComparisonMode ({ commit }) {
      commit('leaveComparisonMode')
    },
    enterComparisonMode ({ state, dispatch, getters, commit }, mapEngine) {
      // Enter comparison mode, enable selected layers, and attach corresponding events
      let baseLayers = getters['config'].map.layers.baseLayers
      const layersToCompare = getters['layersToCompare']

      for (let i = 0; i < layersToCompare.length; i++) {
        let selectedLayer = layersToCompare[i]
        if (selectedLayer === undefined) {
          continue
        }
        Object.keys(baseLayers).forEach(baseLayer => {
          if (selectedLayer.layer === baseLayer) {
            baseLayers[selectedLayer.layer].visible = false
            baseLayers[selectedLayer.layer].layers[0].params.TIME = selectedLayer.time
            var mapLayer = mapEngine.createLayer(baseLayers[selectedLayer.layer])
            mapEngine.addLayer(mapLayer, state.comparisonData.mapLayers)
          }
        })
      }

      let id = 0
      let comparisonGroups = state.comparisonData.mapLayers.getLayers()
      comparisonGroups.forEach(group => {
        let comparisonLayers = group.getLayers()
        group.setVisible(true)
        comparisonLayers.forEach(l => {
          dispatch('attachComparisonSwipeBehaviour', { mapEngine:mapEngine, mapLayer:l, mapLayerId:id })
          id = id + 1
        })
      })

      commit('enterComparisonMode')
    },
    attachComparisonSwipeBehaviour ({ state, getters }, value) {
      let mapEngine = value.mapEngine
      let mapLayer = value.mapLayer
      let mapLayerId = value.mapLayerId

      mapLayer.on('prerender', function (event) {
        const ctx = event.context
        const mapSize = mapEngine.map.getSize()
        const width = mapSize[0] * (getters['comparisonOverlap'] / 100)
        let tl = 0
        let tr = 0
        let bl = 0
        let br = 0
        if (mapLayerId === 0) {
          // left
          tl = getRenderPixel(event, [width, 0])
          tr = getRenderPixel(event, [0, 0])
          bl = getRenderPixel(event, [width, mapSize[1]])
          br = getRenderPixel(event, [0, mapSize[1]])

          ctx.save()
          ctx.beginPath()
          ctx.moveTo(tl[0], tl[1])
          ctx.lineTo(bl[0], bl[1])
          ctx.lineTo(br[0], br[1])
          ctx.lineTo(tr[0], tr[1])
        } else {
          // right
          tl = getRenderPixel(event, [width, 0])
          tr = getRenderPixel(event, [mapSize[0], 0])
          bl = getRenderPixel(event, [width, mapSize[1]])
          br = getRenderPixel(event, mapSize)

          ctx.save()
          ctx.beginPath()
          ctx.moveTo(tl[0], tl[1])
          ctx.lineTo(bl[0], bl[1])
          ctx.lineTo(br[0], br[1])
          ctx.lineTo(tr[0], tr[1])
        }
        ctx.closePath()
        ctx.clip()
      })
      mapLayer.on('postrender', function (event) {
        const ctx = event.context
        ctx.restore()
      })
    },
    initializeLayersToCompareGroup ({ commit }, group) {
      commit('initializeLayersToCompareGroup', group)
    },
    submitFeedback ({ commit, state }, data) {
      const request = 'feedback'
      return new Promise((resolve, reject) => {
        const apiEndpoint = state.config.api.endpoints[request]
        const request_path = apiEndpoint['request_path']
        const response_path = apiEndpoint['response_path']

        const url = state.config.api.url + request_path
        const params = new URLSearchParams()
        Object.keys(data).forEach(key => {
          params.append(key, data[key])
        })
        axios({baseURL: state.config.api.url, url: request_path, method: 'post', params: params})
          .then(resp => {
            if (resp.data.status === 'ok') {
              resolve(resp.data.status)
            } else {
              reject(resp.data.error)
            }
          }).catch(err => {
            reject(err)
          })
        //setTimeout(() => { resolve('ok') }, 1000)
      })
    },
    about ({ commit, state }, about) {
      commit('about', about)
    },
    parcel ({ commit, state }, row) {
      commit('parcel', row)
    }
  },
  getters: {
    config: state => state.config,
    isLoggedIn: state => !!state.token,
    user: state => state.user,
    token: state => state.token,
    status: state => state.status,
    baseLayer: state => state.baseLayer,
    overlayLayer: state => state.overlayLayer,
    tileSetConf: state => state.tileSetConf,
    tileSetProperty: state => state.tileSetProperty,
    searchResults: state => state.searchResults,
    parcel: state => state.parcel,
    parcelStatus: state => state.parcelStatus,
    parcelEvents: state => state.parcelEvents,
    parcelLastRow: state => state.parcelLastRow,
    selectedFeature: state => state.feature,
    timebarValue: state => state.timebar_value,
    filter: state => state.filter,
    chartImageryDate: state => state.chartImagery.date,
    chartImageryDates: state => state.chartImagery.dates,
    chartImageryTsGroup: state => state.chartImagery.tsGroup,
    chartImageryParcelId: state => state.chartImagery.parcelId,
    chartData: state => state.chartData,
    chartDataStorage: state => state.chartDataStorage,
    mapDateIndex: state => state.mapDateIndex,
    mapDate: state => {
      if (state.mapDates.length !== 0) {
        return state.mapDates[state.mapDateIndex][0]
      } else {
        return new Date().toISOString()
      }
    },
    isUiDisabled: state => {
      if (state.baseLayer === 'no_layer')
        return true
      if (state.comparisonData.isComparisonModeActive === true)
        return true
      if (state.mapDates.length === 0)
        return true
    },
    mapTime: state => state.mapTime,
    mapDates: state => state.mapDates,
    mapDatesList: state => {
      return state.mapDates.map(d => d[0])
    },
    layersToCompare: state => state.comparisonData.layers,
    comparisonDataUpdated: state => state.comparisonData.updated,
    isComparisonModeActive: state => state.comparisonData.isComparisonModeActive,
    comparisonOverlap: state => state.comparisonData.overlap,
    aboutOpen: state => state.about,
    searchIndex: state => state.searchIndex
  }
})
